/**
 * Szene, Kamera, Licht, Steuerung.
 *
 * Bewusst schlicht gehalten: eine Sonne mit weichem Schatten, ein Himmelslicht als
 * Aufhellung, ein Gegenlicht gegen tote schwarze Flaechen. Alles in Millimetern,
 * damit die Kamerawerte zu den Brettmassen passen.
 */
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { BRETT } from '../core/data/spielplan'

export interface Bühne {
  readonly renderer: THREE.WebGLRenderer
  readonly szene: THREE.Scene
  readonly kamera: THREE.PerspectiveCamera
  readonly steuerung: OrbitControls
  /** Rendert genau ein Bild. */
  zeichne(): void
  /** Passt die Groesse an den Container an. */
  passeAn(): void
}

export function baueBuehne(behaelter: HTMLElement): Bühne {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.05
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  behaelter.appendChild(renderer.domElement)

  const szene = new THREE.Scene()
  szene.background = new THREE.Color('#161a21')
  szene.fog = new THREE.Fog('#161a21', 1400, 2600)

  // Der Tisch, auf dem das Brett liegt.
  const tisch = new THREE.Mesh(
    new THREE.PlaneGeometry(4000, 4000),
    new THREE.MeshStandardMaterial({ color: '#2c2118', roughness: 0.95 }),
  )
  tisch.rotation.x = -Math.PI / 2
  tisch.position.y = -16
  tisch.receiveShadow = true
  szene.add(tisch)

  // Die Near-Plane bewusst weit weg: sie entscheidet ueber die Tiefengenauigkeit.
  // Mit 10 mm fangen flache, uebereinanderliegende Brettflaechen an zu flimmern.
  const kamera = new THREE.PerspectiveCamera(38, 1, 40, 3200)
  kamera.position.set(0, 700, 620)

  const himmel = new THREE.HemisphereLight('#dfe9f5', '#6b5a46', 1.15)
  szene.add(himmel)

  const sonne = new THREE.DirectionalLight('#fff4e2', 2.1)
  sonne.position.set(-320, 620, 340)
  sonne.castShadow = true
  sonne.shadow.mapSize.set(2048, 2048)
  sonne.shadow.bias = -0.0006
  sonne.shadow.normalBias = 0.6
  const s = sonne.shadow.camera
  s.left = -BRETT.breite * 0.75
  s.right = BRETT.breite * 0.75
  s.top = BRETT.tiefe * 0.9
  s.bottom = -BRETT.tiefe * 0.9
  s.near = 50
  s.far = 1800
  s.updateProjectionMatrix()
  szene.add(sonne)

  const gegenlicht = new THREE.DirectionalLight('#c9dcff', 0.45)
  gegenlicht.position.set(420, 380, -420)
  szene.add(gegenlicht)

  const steuerung = new OrbitControls(kamera, renderer.domElement)
  steuerung.enableDamping = true
  steuerung.dampingFactor = 0.08
  steuerung.minDistance = 300
  steuerung.maxDistance = 1600
  steuerung.maxPolarAngle = Math.PI * 0.46
  steuerung.target.set(0, 0, 0)
  steuerung.update()

  /**
   * Der Abstand, bei dem das ganze Brett gerade ins Bild passt.
   *
   * Statt zu schaetzen wird gemessen: die acht Eckpunkte des Brettquaders werden
   * projiziert, und der Abstand wird per Intervallhalbierung so gewaehlt, dass alle
   * acht mit etwas Rand im Bild liegen. Das funktioniert bei jedem Seitenverhaeltnis,
   * auch hochkant auf dem Telefon, wo nicht die Hoehe, sondern die Breite begrenzt.
   */
  const ECKEN = (() => {
    const hb = BRETT.breite / 2
    const ht = BRETT.tiefe / 2
    const punkte: THREE.Vector3[] = []
    for (const x of [-hb, hb]) {
      for (const y of [-16, 90]) {
        for (const z of [-ht, ht]) punkte.push(new THREE.Vector3(x, y, z))
      }
    }
    return punkte
  })()

  function passtBeiAbstand(abstand: number): boolean {
    const richtung = kamera.position.clone().sub(steuerung.target).normalize()
    const probe = kamera.clone()
    probe.position.copy(steuerung.target).addScaledVector(richtung, abstand)
    probe.lookAt(steuerung.target)
    probe.updateMatrixWorld(true)
    probe.updateProjectionMatrix()
    const rand = 0.94
    return ECKEN.every((e) => {
      const v = e.clone().project(probe)
      return Math.abs(v.x) <= rand && Math.abs(v.y) <= rand && v.z < 1
    })
  }

  function idealerAbstand(): number {
    let lo = 200
    let hi = 3000
    for (let i = 0; i < 24; i++) {
      const m = (lo + hi) / 2
      if (passtBeiAbstand(m)) hi = m
      else lo = m
    }
    return hi
  }

  let letzterIdealabstand = 0
  let ersteAnpassung = true

  function passeAn(): void {
    const b = Math.max(1, behaelter.clientWidth)
    const h = Math.max(1, behaelter.clientHeight)
    renderer.setSize(b, h, false)
    kamera.aspect = b / h
    kamera.updateProjectionMatrix()

    if (ersteAnpassung) {
      // Je schmaler das Fenster, desto steiler der Blick: von schraeg vorn ist das
      // Brett breit und flach und laesst auf einem hohen Fenster viel Platz leer,
      // von weiter oben passt seine Form besser zur Flaeche. Danach gehoert die
      // Kamera dem Spieler - eine Groessenaenderung dreht sie nicht mehr.
      const hoehenwinkel = kamera.aspect >= 1.5 ? 48 : kamera.aspect >= 1.2 ? 56 : 66
      const b2 = (hoehenwinkel * Math.PI) / 180
      const r = kamera.position.distanceTo(steuerung.target)
      kamera.position.set(
        steuerung.target.x,
        steuerung.target.y + Math.sin(b2) * r,
        steuerung.target.z + Math.cos(b2) * r,
      )
      kamera.lookAt(steuerung.target)
      ersteAnpassung = false
    }

    const ideal = idealerAbstand()
    const blick = kamera.position.clone().sub(steuerung.target)
    // Eine eigene Zoomstufe des Spielers bleibt als Verhaeltnis erhalten.
    const eigenerZoom = letzterIdealabstand > 0 ? blick.length() / letzterIdealabstand : 1
    blick.setLength(ideal * eigenerZoom)
    kamera.position.copy(steuerung.target).add(blick)
    letzterIdealabstand = ideal
    steuerung.update()
  }

  function zeichne(): void {
    steuerung.update()
    renderer.render(szene, kamera)
  }

  passeAn()
  return { renderer, szene, kamera, steuerung, zeichne, passeAn }
}
